export type SchemeLevel = { score: number; label: string; color: string };
export type GridWindow = 'boy' | 'moy' | 'eoy';
export type GridDomainKey = 'speaking' | 'writing';
export type GridWindowScores = Record<GridWindow, number | null>;
export type GridIndicator = { name: string; scores: GridWindowScores };
export type GridOnTrack = { later: 'moy' | 'eoy'; onTrack: boolean } | null;
export type GridDomain = { domain: GridDomainKey; indicators: GridIndicator[]; average: GridWindowScores; onTrack?: GridOnTrack; windows?: Partial<Record<GridWindow, string>> };
export type GridYear = { schoolYear: number; benchmarks: GridDomain[]; windows?: Partial<Record<GridWindow, string>>; scheme?: SchemeLevel[] };
export type GridBand = { score: number; min: number; max: number; label: string; color: string };

export type GridColumn = { schoolYear: number; window: GridWindow; month: string | null };
export type GridRow = { name: string; values: (number | null)[] };
export type GridDomainModel = {
  domain: GridDomainKey;
  label: string;
  average: (number | null)[];
  months: (string | null)[];
  indicators: GridRow[];
  onTrack: { schoolYear: number; result: NonNullable<GridOnTrack> }[];
};
export type GridModel = { years: [number, number]; schemes: [SchemeLevel[] | null, SchemeLevel[] | null]; columns: GridColumn[]; domains: GridDomainModel[]; hasData: boolean };

export const GRID_WINDOWS: GridWindow[] = ['boy', 'moy', 'eoy'];
export const GRID_DOMAINS: { key: GridDomainKey; label: string }[] = [
  { key: 'speaking', label: 'Speaking' },
  { key: 'writing', label: 'Writing' },
];
export const EMPTY_CELL = '–';
const INK = '#08081B';
const INVERTED = '#FFFFFF';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function bandsOf(scheme: SchemeLevel[]): GridBand[] {
  const sorted = [...scheme].sort((a, b) => a.score - b.score);
  const top = sorted.length;
  return sorted.map((level, i) => ({
    score: level.score,
    min: i === 0 ? 1 : Math.round((sorted[i - 1]!.score + 0.5) * 10) / 10,
    max: i === top - 1 ? level.score : Math.round((level.score + 0.4) * 10) / 10,
    label: level.label,
    color: level.color,
  }));
}

export function levelOf(bands: GridBand[], value: number | null): number {
  if (value == null || bands.length === 0) return 0;
  const rounded = Math.round(value * 10) / 10;
  const index = bands.findIndex((band) => rounded >= band.min && rounded <= band.max);
  if (index >= 0) return index + 1;
  return rounded > bands[bands.length - 1]!.max ? bands.length : 0;
}

export function cellText(value: number | null, average: boolean): string {
  if (value == null) return EMPTY_CELL;
  const rounded = Math.round(value * 10) / 10;
  if (average || !Number.isInteger(rounded)) return rounded.toFixed(1);
  return String(rounded);
}

export function cellInk(color: string): string {
  const hex = color.replace('#', '');
  if (hex.length !== 6) return INK;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const luminance = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  return luminance > 0.7 ? INK : INVERTED;
}

export function windowLabel(month: string | undefined | null): string {
  if (!month) return '';
  const [year, mm] = month.split('-');
  const name = MONTHS[Number(mm) - 1];
  if (!name || !year) return '';
  return `${name} ${year.slice(2)}`;
}

export function yearLabel(endYear: number): string {
  return `${endYear - 1}–${String(endYear).slice(2)}`;
}

export function calendarSchoolYearEnd(now: Date = new Date()): number {
  return now.getUTCMonth() >= 6 ? now.getUTCFullYear() + 1 : now.getUTCFullYear();
}

function valuesOf(scores: GridWindowScores | undefined): (number | null)[] {
  return GRID_WINDOWS.map((window) => scores?.[window] ?? null);
}

export function gridModel(years: GridYear[], thisYear: number): GridModel {
  const shown: [number, number] = [thisYear - 1, thisYear];
  const byYear = new Map(years.filter((y) => shown.includes(y.schoolYear)).map((y) => [y.schoolYear, y]));
  const columns: GridColumn[] = shown.flatMap((schoolYear) =>
    GRID_WINDOWS.map((window) => ({ schoolYear, window, month: byYear.get(schoolYear)?.windows?.[window] ?? null })),
  );
  const domains = GRID_DOMAINS.map(({ key, label }) => {
    const perYear = shown.map((schoolYear) => byYear.get(schoolYear)?.benchmarks.find((d) => d.domain === key));
    const names: string[] = [];
    for (const domain of perYear) {
      for (const indicator of domain?.indicators ?? []) {
        if (!names.includes(indicator.name)) names.push(indicator.name);
      }
    }
    const indicators = names.map((name) => ({
      name,
      values: perYear.flatMap((domain) => valuesOf(domain?.indicators.find((i) => i.name === name)?.scores)),
    }));
    const onTrack = shown
      .map((schoolYear, i) => ({ schoolYear, result: perYear[i]?.onTrack ?? null }))
      .filter((entry): entry is { schoolYear: number; result: NonNullable<GridOnTrack> } => entry.result != null);
    const months = shown.flatMap((schoolYear, i) =>
      GRID_WINDOWS.map((window) => {
        const own = perYear[i]?.windows?.[window] ?? null;
        return own && own !== (byYear.get(schoolYear)?.windows?.[window] ?? null) ? own : null;
      }),
    );
    return { domain: key, label, average: perYear.flatMap((domain) => valuesOf(domain?.average)), months, indicators, onTrack };
  });
  const hasData = domains.some((d) => d.indicators.length > 0 || d.average.some((v) => v != null));
  const schemes = shown.map((schoolYear) => byYear.get(schoolYear)?.scheme ?? null) as [SchemeLevel[] | null, SchemeLevel[] | null];
  return { years: shown, schemes, columns, domains, hasData };
}

export type OnTrackChip = { label: string; tone: 'good' | 'warn'; title: string };

export function onTrackChip(domain: GridDomainModel): OnTrackChip | null {
  const newest = [...domain.onTrack].sort((a, b) => b.schoolYear - a.schoolYear)[0];
  if (!newest) return null;
  return {
    label: newest.result.onTrack ? 'On track' : 'Not on track',
    tone: newest.result.onTrack ? 'good' : 'warn',
    title: `${yearLabel(newest.schoolYear)} BOY to ${newest.result.later.toUpperCase()}`,
  };
}
